import React from 'react';
import { FC, memo, useCallback } from 'react';
import { IAttribute } from '../../../Interfaces/IAttribute';
import { ISwatch } from '../../../Interfaces/ISwatch';
import { ISelectedAttributes, ISelectedSwatch } from './Configurator';
import { HTML_CLASSES } from '../../../../../constants/product.constants';

interface IAttributeOptionProps {
    attribute: IAttribute;
    swatches: Record<string, Record<string, ISwatch>>;
    selectedAttributes: ISelectedAttributes[];
    selectAttributes: (attributes: ISelectedAttributes[]) => void;
    defaultSelectedMainSwatch: ISelectedSwatch;
    setDefaultSelectedMainSwatch: (attributes: ISelectedSwatch) => void;
}

export const AttributeOption: FC<IAttributeOptionProps> = memo(
    ({
        attribute,
        swatches,
        selectedAttributes,
        selectAttributes,
        defaultSelectedMainSwatch,
        setDefaultSelectedMainSwatch,
    }) => {
        if (!attribute) {
            return null;
        }

        const getHasMappedSwatch = useCallback(
            (attributeId: string, optionId: string): ISwatch | undefined => {
                return swatches?.[attributeId]?.[optionId];
            },
            [swatches],
        );

        const getSelectedAttribute = useCallback(
            (attributeId: string, optionId: string): ISelectedAttributes | undefined => {
                return selectedAttributes?.find((selectedAttribute) => {
                    return selectedAttribute.attributeId === attributeId && selectedAttribute.optionId === optionId;
                });
            },
            [selectedAttributes],
        );

        const selectAttribute = useCallback(
            (attributeId: string, optionId: string, products?: string[]) => {
                // Type guard for products array
                const safeProducts = products || [];

                if (attributeId === defaultSelectedMainSwatch.attributeId) {
                    setDefaultSelectedMainSwatch({
                        attributeId: attributeId,
                        optionId: optionId,
                        products: safeProducts,
                    });
                }

                // Immutable update instead of mutation
                const newlySelectedAttributes = selectedAttributes.map((selectedAttribute) =>
                    selectedAttribute.attributeId === attributeId
                        ? { ...selectedAttribute, optionId }
                        : selectedAttribute,
                );
                selectAttributes(newlySelectedAttributes);
            },
            [selectedAttributes, selectAttributes, defaultSelectedMainSwatch.attributeId, setDefaultSelectedMainSwatch],
        );

        return (
            <>
                <div className={HTML_CLASSES.PRODUCT_CONFIGURATOR_TITLE} id={`attribute-${attribute.id}-title`}>
                    {attribute.label}
                </div>
                <ul
                    className={HTML_CLASSES.PRODUCT_CONFIGURATOR_GROUP}
                    role="radiogroup"
                    aria-labelledby={`attribute-${attribute.id}-title`}
                >
                    {attribute.options?.map((option) => {
                        const color = getHasMappedSwatch(attribute.id, option.id);
                        const isSelected = !!getSelectedAttribute(attribute.id, option.id);
                        const radioId = `option-${attribute.id}-${option.id}`;
                        const ariaLabel = color?.value
                            ? `${attribute.label}: ${option.label} color option`
                            : `${attribute.label}: ${option.label}`;

                        return (
                            <li key={option.id}>
                                <label htmlFor={radioId}>
                                    <input
                                        id={radioId}
                                        type="radio"
                                        name={`attribute-${attribute.id}`}
                                        value={option.id}
                                        checked={isSelected}
                                        aria-label={ariaLabel}
                                        onChange={() => selectAttribute(attribute.id, option.id, option.products)}
                                    />
                                    <span className={HTML_CLASSES.LABEL}>
                                        {color?.value && (
                                            <span
                                                className={HTML_CLASSES.SWATCH}
                                                style={{ background: color.value }}
                                                aria-hidden="true"
                                            />
                                        )}
                                        {option.label}
                                    </span>
                                </label>
                            </li>
                        );
                    })}
                </ul>
            </>
        );
    },
);

AttributeOption.displayName = 'AttributeOption';
